import {Inject, Injectable, DOCUMENT} from '@angular/core';
import {UserInterface} from '../model/user.interface';
import {NavConfigInterface} from '../model/nav-config.interface';
import {MenuItem} from 'primeng/api';

import {UserService} from './user.service';
import {AccountService} from './account.service';
import { HttpClient } from '@angular/common/http';
import {AccountInterface} from '../model/account.interface';
import {firstValueFrom} from 'rxjs';
import {MpopFeatureModel} from '../model/mpop-feature.model';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  documentHostname: string;
  documentProtocol: string;
  urlShortcodeMap = {
    '[ACCOUNT_ID]': 'accountId',
    '[ACCOUNT_WEBSITE_URL]': 'accountWebsiteUrl'
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private userService: UserService,
    private accountService: AccountService,
    private http: HttpClient
  ) {
    this.documentHostname = this.document.location.hostname;
    this.documentProtocol = this.document.location.protocol;
  }

  /** Uses one account snapshot throughout each asynchronous menu build. */
  public async getMenuItems(
    accountId: number | string,
    configurationFilename: string,
    account?: AccountInterface
  ): Promise<MenuItem[]>
  {
    const configurationPath = `assets/configurations/menus/${configurationFilename}`;
    const navConfig = await firstValueFrom(this.http.get<NavConfigInterface>(configurationPath));
    const currentUser = await firstValueFrom(this.userService.getUser());
    const user = {...currentUser};
    if (!account && accountId) {
      account = await firstValueFrom(this.accountService.getAccount(Number(accountId)));
    }
    if (accountId) {
      user.accountId = Number(accountId);
      if (account) {
        user.accountWebsiteUrl = account.websiteUrl;
      }
    }
    const featureFlags = await firstValueFrom(this.userService.getAllMpopFeatures());
    const menuItems = this.buildMenuRecursive(user, navConfig.items, featureFlags, account ?? null);

    return menuItems;
  }

  /** Carries the same account through nested menus while preserving feature and access checks. */
  private buildMenuRecursive(
    user: UserInterface,
    navConfigItems: NavConfigInterface[] | undefined,
    featureFlags: MpopFeatureModel[],
    account: AccountInterface | null
  ): MenuItem[]
  {
    const completedMenuItems: MenuItem[] = [];
    for (const navItemConfig of navConfigItems ?? []) {
      const menuItem = this.buildMenuItem(user, navItemConfig, featureFlags, account);
      if (menuItem && navItemConfig.items && navItemConfig.items.length > 0) {
        menuItem.items = this.buildMenuRecursive(user, navItemConfig.items, featureFlags, account);
      }
      if (this.menuItemIsValid(menuItem)) {
        completedMenuItems.push(menuItem);
      }
    }

    return completedMenuItems;
  }

  /** Builds an eligible item from request-local account, user, and feature state. */
  private buildMenuItem(
    user: UserInterface,
    navItemConfig: NavConfigInterface,
    featureFlags: MpopFeatureModel[],
    account: AccountInterface | null
  ): MenuItem | null
  {
    let menuItem: MenuItem | null = null;
    const hasFeatureFlagOn = this.featureFlagSatisfied(navItemConfig, featureFlags);
    const hasAccountFlagOn = this.accountFlagSatisfied(navItemConfig, account);
    const hasAccess = this.userService.userHasAccessKeys(user, user.accountId, navItemConfig.userAccessKeys);
    if (hasAccess && hasFeatureFlagOn && hasAccountFlagOn) {
      menuItem = this.createMenuItem(user, navItemConfig);
    }

    return menuItem;
  }

  /** Preserves configured links and URL substitutions after eligibility has been checked. */
  private createMenuItem(user: UserInterface, navItemConfig: NavConfigInterface): MenuItem
  {
    const menuItem: MenuItem = {
      id: navItemConfig.id,
      label: navItemConfig.label,
      target: navItemConfig.target,
      routerLink: null,
      url: this.buildUrl(user, navItemConfig.url),
      icon: navItemConfig.icon,
      routerLinkActiveOptions: {
        exact: true
      }
    };

    if ('routerLink' in navItemConfig && navItemConfig.routerLink !== '') {
      menuItem.url = null;
      menuItem.routerLink = [
        navItemConfig.routerLink,
        user.accountId
      ];
    }

    return menuItem;
  }

  /**
   * Checks if the feature flag is enabled
   */
  private featureFlagSatisfied(navItemConfig: NavConfigInterface, featureFlags: MpopFeatureModel[]): boolean
  {
    let isEnabled = true;
    if (navItemConfig.featureFlag) {
      const matchingFeature = featureFlags.find((feature) => feature.name === navItemConfig.featureFlag);
      isEnabled = matchingFeature?.enabled === true;
    }

    return isEnabled === true;
  }

  /** Evaluates account flags without borrowing state from another in-flight menu request. */
  private accountFlagSatisfied(navItemConfig: NavConfigInterface, account: AccountInterface | null): boolean
  {
    let isEnabled = true;
    if (navItemConfig.accountFlag) {
      isEnabled = account !== null && !!account[navItemConfig.accountFlag];
    }

    return isEnabled;
  }

  private menuItemIsValid(menuItem: MenuItem): boolean {
    return menuItem !== null && ((this.menuItemHasLink(menuItem)) || this.menuItemHasSubItems(menuItem));
  }

  private menuItemHasLink(menuItem: MenuItem): boolean {
    return menuItem.url || menuItem.routerLink;
  }

  private menuItemHasSubItems(menuItem: MenuItem) {
    return 'items' in menuItem && menuItem.items.length > 0;
  }

  private buildUrl(user: UserInterface, urlTemplate: string): string {
    let result = null;
    if (urlTemplate) {
      result = this.replaceUrlShortcode(user, urlTemplate);
      if (urlTemplate !== '[ACCOUNT_WEBSITE_URL]') {
        result = `${this.documentProtocol}//${this.documentHostname}${result}`;
      }
    }

    return result;
  }

  private replaceUrlShortcode(user: UserInterface, urlTemplate: string): string {
    Object.keys(this.urlShortcodeMap).forEach((shortcode) => {
      if (urlTemplate.includes(shortcode) && user[this.urlShortcodeMap[shortcode]]) {
        const shortCodeExpression = RegExp('(\\' + shortcode + ')', 'g');
        urlTemplate = urlTemplate.replace(shortCodeExpression, user[this.urlShortcodeMap[shortcode]]);
      }
    });

    return urlTemplate;
  }
}
